/**
 * UserMenu - Shared user portrait + dropdown menu.
 *
 * Consistent top-right user control used across:
 * - PublicNavbar (landing, pricing, docs, benchmark)
 * - DashboardLayout (stock analysis, portfolio, strategic, profile, preferences, plans)
 * - VerdictDashboardPage (general research)
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  User,
  ChevronDown,
  LayoutDashboard,
  Settings,
  LogOut,
  CreditCard,
  Coins,
  Infinity,
  Sun,
  Moon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';

interface UserMenuProps {
  user: { email: string; first_name?: string; last_name?: string };
  onLogout: () => void;
  /** Credit balance (omit to hide credits row) */
  creditBalance?: number | null;
  /** Whether user has unlimited credits */
  isUnlimited?: boolean;
  /** Show "Console" link (used on public pages to link into dashboard) */
  showConsoleLink?: boolean;
  className?: string;
}

export function UserMenu({
  user,
  onLogout,
  creditBalance,
  isUnlimited = false,
  showConsoleLink = false,
  className,
}: UserMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme, setMode } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    if (open) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    setOpen(false);
    const currentPath = location.pathname;
    onLogout();
    if (currentPath === '/preferences') {
      navigate('/dashboard');
    } else {
      navigate(currentPath);
    }
  };

  return (
    <div ref={ref} className={cn('relative', className)}>
      {/* Avatar trigger */}
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors',
          open
            ? 'bg-surface-light text-text-primary'
            : 'hover:bg-surface-light text-text-secondary hover:text-text-primary'
        )}
      >
        <div className="w-7 h-7 rounded-full bg-verdict-primary/20 border border-verdict-primary/30 flex items-center justify-center">
          <User className="w-4 h-4 text-verdict-primary" />
        </div>
        <ChevronDown
          className={cn(
            'w-3.5 h-3.5 transition-transform',
            open && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-52 bg-surface-overlay border border-border rounded-lg shadow-xl py-1 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          {/* User identity */}
          <div className="px-3 py-2 border-b border-border-subtle">
            <p className="text-[11px] text-text-muted">Signed in as</p>
            <p className="text-sm text-text-primary truncate">{user.email}</p>
          </div>

          {/* Credit balance (subtle) */}
          {creditBalance != null && (
            <button
              onClick={() => go('/dashboard/plans')}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-text-muted hover:text-text-secondary hover:bg-surface-light transition-colors"
            >
              {isUnlimited ? (
                <>
                  <Infinity className="w-4 h-4 text-verdict-primary" />
                  <span className="text-xs">Unlimited</span>
                </>
              ) : (
                <>
                  <Coins className="w-4 h-4" />
                  <span className="text-xs">{creditBalance.toLocaleString()} credits</span>
                </>
              )}
            </button>
          )}

          {/* Console link (public pages only) */}
          {showConsoleLink && (
            <button
              onClick={() => go('/dashboard')}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light transition-colors"
            >
              <LayoutDashboard className="w-4 h-4" />
              Console
            </button>
          )}

          {/* Settings */}
          <button
            onClick={() => go('/preferences')}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light transition-colors"
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>

          {/* Theme toggle */}
          <button
            onClick={() => setMode(resolvedTheme === 'dark' ? 'light' : 'dark')}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light transition-colors"
            role="switch"
            aria-checked={resolvedTheme === 'light'}
            aria-label="Toggle light mode"
          >
            {resolvedTheme === 'dark' ? (
              <>
                <Sun className="w-4 h-4" />
                Light mode
              </>
            ) : (
              <>
                <Moon className="w-4 h-4" />
                Dark mode
              </>
            )}
          </button>

          {/* Plans */}
          <button
            onClick={() => go('/dashboard/plans')}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light transition-colors"
          >
            <CreditCard className="w-4 h-4" />
            Plans
          </button>

          {/* Divider + Logout */}
          <div className="border-t border-border-subtle mt-1 pt-1">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-error hover:text-error/80 hover:bg-error/10 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Log out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
